import { useSyncExternalStore } from 'react';
import { PlanProductRuntimeCharacteristic } from '../../components/PlaceOrder/utils/characteristicsFlagsUtilsCoCo.js';
import { getShoppingBasketLocalStorageKeyCoCo } from '../../selfservice/common/localStorageUtilsCoCo.js';
import { ShoppingBasketCoCoScope } from '../enums.js';
import { getLocale } from '../i18n';
import { isInBrowser } from '../utils/ssrUtils.js';
import type { ProductOfferingCharacteristic } from '../../generated/api/productOfferingCharacteristic.js';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../types/shoppingBasketCoCo.js';

const createEmptyBasket = (): ShoppingBasketType => {
  return {
    cartId: '',
    created: new Date().toISOString(),
    productOfferings: [],
    uiState: {
      lastSaved: new Date().toISOString(),
    },
  };
};

/** Shape written by earlier releases: a single selection object with UI-only `label` and `options`. */
interface LegacyRuntimeCharacteristicSelection {
  value: string;
  label?: string;
  options?: Array<{ value: string; label?: string }>;
}

const isLegacyRuntimeCharacteristic = (
  characteristic: unknown
): characteristic is LegacyRuntimeCharacteristicSelection =>
  typeof characteristic === 'object' &&
  characteristic !== null &&
  !Array.isArray(characteristic) &&
  typeof (characteristic as { value?: unknown }).value === 'string';

/**
 * Converts baskets persisted in localStorage by earlier releases to the catalog structure
 * (`ProductOfferingCharacteristic[]`). Without this, previously stored baskets would either crash
 * the basket page or silently drop the selected fiber mode from the order.
 */
const migrateRuntimeCharacteristics = (item: ProductOfferingCartItems): ProductOfferingCartItems => {
  const legacy: unknown = item.productOfferingRuntimeCharacteristic;
  if (!isLegacyRuntimeCharacteristic(legacy)) {
    return item;
  }
  const name = PlanProductRuntimeCharacteristic.FIBER_MODE;
  const selection: ProductOfferingCharacteristic[] = [{ name, type: 'String', value: [{ value: legacy.value }] }];
  const hasCatalogOptions = item.planProductOffering?.productOfferingRuntimeCharacteristic?.length;
  if (!item.planProductOffering || hasCatalogOptions) {
    return { ...item, productOfferingRuntimeCharacteristic: selection };
  }
  // The legacy labels were resolved with the locale active when the item was added, which for a
  // basket restored in the same browser is the locale still in use.
  const locale = getLocale();
  return {
    ...item,
    productOfferingRuntimeCharacteristic: selection,
    planProductOffering: {
      ...item.planProductOffering,
      productOfferingRuntimeCharacteristic: [
        {
          name,
          type: 'String',
          value: (legacy.options ?? [{ value: legacy.value, label: legacy.label }]).map(option => ({
            value: option.value,
            ['_default']: option.value === legacy.value,
            ...(option.label ? { localizedValue: [{ locale, value: option.label }] } : {}),
          })),
        },
      ],
    },
  };
};

export const parseBasketJson = (rawBasketData: string | null): ShoppingBasketType => {
  try {
    if (rawBasketData) {
      const basket = JSON.parse(rawBasketData) as ShoppingBasketType;
      if (!basket.cartId) {
        basket.cartId = crypto.randomUUID();
        basket.created = new Date().toISOString();
      }
      return { ...basket, productOfferings: (basket.productOfferings ?? []).map(migrateRuntimeCharacteristics) };
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse basket JSON:', error);
  }
  return createEmptyBasket();
};

export const getShoppingBasketFromLocalStorage = (scope: ShoppingBasketCoCoScope = ShoppingBasketCoCoScope.ADMIN) => {
  if (isInBrowser()) {
    return localStorage.getItem(getShoppingBasketLocalStorageKeyCoCo(scope));
  }
  return '';
};

export const handleShoppingBasketUpdate = (
  basket: string,
  scope: ShoppingBasketCoCoScope = ShoppingBasketCoCoScope.ADMIN
) => {
  const storageKey = getShoppingBasketLocalStorageKeyCoCo(scope);
  const prevBasket = getShoppingBasketFromLocalStorage(scope);
  // Only set the basket when there's actual changes, otherwise syncing cart via LocalStorage between multiple tabs
  // will cause infinite loop.
  if (prevBasket !== basket) {
    const parsedBasket = parseBasketJson(basket);
    const timestamp = new Date().toISOString();
    if (!parsedBasket.created) {
      parsedBasket.created = timestamp;
    } else if (parsedBasket.uiState) {
      parsedBasket.uiState.lastSaved = timestamp;
    }

    const parsedBasketAsString = JSON.stringify(parsedBasket);

    if (isInBrowser()) {
      localStorage.setItem(storageKey, parsedBasketAsString);
    }
    globalThis.dispatchEvent(new StorageEvent('storage', { key: storageKey, newValue: parsedBasketAsString }));
  }
};

export const useShoppingBasketCoCo = (scope: ShoppingBasketCoCoScope = ShoppingBasketCoCoScope.ADMIN) => {
  const storageKey = getShoppingBasketLocalStorageKeyCoCo(scope);
  const setShoppingBasket = (newValue: string) => {
    if (isInBrowser()) {
      handleShoppingBasketUpdate(newValue, scope);
    }
  };

  if (!getShoppingBasketFromLocalStorage(scope)) {
    if (isInBrowser()) {
      localStorage.setItem(storageKey, JSON.stringify(createEmptyBasket()));
    }
  }
  const getSnapshot = () => getShoppingBasketFromLocalStorage(scope);

  const subscribe = (listener: () => void) => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        listener();
      }
    };
    globalThis.addEventListener('storage', handleStorage);
    return () => {
      globalThis.removeEventListener('storage', handleStorage);
    };
  };

  const shoppingBasket = useSyncExternalStore(subscribe, getSnapshot, () => '');

  return { shoppingBasket, setShoppingBasket };
};
